import React, { useCallback, useState } from 'react';
import styled, { css } from 'styled-components';
import type { FormikProps } from 'formik';

import { Row, Alert, Button } from 'components/bootstrap';
import { HIDE_DL_SEARCH_EXPIRED_RESULT_ALERT } from 'data-lake/preview/Constants';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';
import useSendTelemetry from 'logic/telemetry/useSendTelemetry';
import Store from 'logic/local-storage/Store';
import type { BaseLakeSearchType, BaseSearchBarFormValues } from 'data-lake/preview/Types';
import type Search from 'views/logic/search/Search';

const Container = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledAlert = styled(Alert)`
  margin-top: 0;
`;
const StyledButton = styled(Button)(
  ({ theme }) => css`
    margin-right: ${theme.spacings.xs};
  `,
);

type Props<SearchBarFormValues extends BaseSearchBarFormValues> = {
  expiredSearch: Search;
  searchBarFormRef: React.RefObject<FormikProps<SearchBarFormValues>>;
  filtersFromSearchType: (searchType: BaseLakeSearchType) => SearchBarFormValues;
};

const ExpiredSearchInfo = <SearchBarFormValues extends BaseSearchBarFormValues>({
  expiredSearch,
  searchBarFormRef,
  filtersFromSearchType,
}: Props<SearchBarFormValues>) => {
  const sendTelemetry = useSendTelemetry();
  const [hideExplanation, setHideExplanation] = useState(Store.get(HIDE_DL_SEARCH_EXPIRED_RESULT_ALERT) === 'true');

  const hideFeatureExplanation = useCallback(() => {
    Store.set(HIDE_DL_SEARCH_EXPIRED_RESULT_ALERT, 'true');
    setHideExplanation(true);
  }, []);

  const restartExpiredSearch = useCallback(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.PREVIEW.APPLY_FILTER_FROM_EXPIRED_SEARCH, {
      app_pathname: '/data-storage/preview',
      app_section: 'expired-info',
    });

    const searchType = expiredSearch.queries.first().searchTypes[0] as BaseLakeSearchType;
    searchBarFormRef.current?.setValues(filtersFromSearchType(searchType));
  }, [expiredSearch.queries, filtersFromSearchType, searchBarFormRef, sendTelemetry]);

  if (hideExplanation) {
    return null;
  }

  return (
    <Row>
      <StyledAlert bsStyle="default" onDismiss={hideFeatureExplanation}>
        <Container>
          <span>
            Your previous search has expired. Search results are stored for 24 hours and have now been cleared. You can
            reapply the previous filters or start a completely new search.
          </span>
          <StyledButton bsSize="xsmall" bsStyle="info" onClick={restartExpiredSearch}>
            Reapply filters
          </StyledButton>
        </Container>
      </StyledAlert>
    </Row>
  );
};

export default ExpiredSearchInfo;
